/**
 * NewRelic utilities for user tracking and custom events
 */

/**
 * Set the user ID for NewRelic session replay and tracking
 * @param {Object|string} user - User object or user ID string
 */
export const setNewRelicUserId = (user) => {
  if (window.newrelic && user) {
    // Handle both user objects and direct user ID strings
    const userId = typeof user === 'string' ? user : (user.userId || user.email || user.id);
    if (userId) {
      window.newrelic.setUserId(userId);
      console.log('NewRelic user ID set:', userId);
      
      // Add additional user attributes if user is an object
      if (typeof user === 'object') {
        const userAttributes = {};
        
        // Add common user attributes
        if (user.email) userAttributes.userEmail = user.email;
        if (user.role) userAttributes.userRole = user.role;
        if (user.status) userAttributes.userStatus = user.status;
        if (user.loginCount) userAttributes.loginCount = user.loginCount;
        if (user.lastLoginAt) userAttributes.lastLoginAt = user.lastLoginAt;
        
        // Add custom attributes to NewRelic
        Object.entries(userAttributes).forEach(([key, value]) => {
          window.newrelic.setCustomAttribute(key, value);
        });
        
        console.log('NewRelic user attributes set:', userAttributes);
      }
    }
  }
};

/**
 * Clear the NewRelic user ID (useful for logout)
 */
export const clearNewRelicUserId = () => {
  if (window.newrelic) {
    window.newrelic.setUserId(null);
    console.log('NewRelic user ID cleared');
  }
};

/**
 * Add custom attributes to NewRelic for additional context
 * @param {Object} attributes - Key-value pairs of attributes to add
 */
export const addNewRelicAttributes = (attributes) => {
  if (window.newrelic && attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      window.newrelic.addPageAction('custom_attribute', { [key]: value });
    });
  }
};

/**
 * Record a custom event in NewRelic
 * @param {string} eventName - Name of the event
 * @param {Object} attributes - Additional attributes for the event
 */
export const recordNewRelicEvent = (eventName, attributes = {}) => {
  if (window.newrelic) {
    window.newrelic.addPageAction(eventName, attributes);
  }
};

/**
 * Track user interactions for better session replay context
 * @param {string} action - The action performed (e.g., 'chat_sent', 'model_changed')
 * @param {Object} details - Additional details about the action
 */
export const trackUserAction = (action, details = {}) => {
  if (window.newrelic) {
    window.newrelic.addPageAction('user_action', {
      action,
      timestamp: new Date().toISOString(),
      ...details
    });
  }
};

/**
 * Track errors with user context
 * @param {Error|string} error - The error to track
 * @param {Object} context - Additional context about the error
 */
export const trackError = (error, context = {}) => {
  if (window.newrelic) {
    const errorMessage = error instanceof Error ? error.message : error;
    window.newrelic.noticeError(error, {
      ...context,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Update NewRelic session replay block selector to exclude additional elements
 * @param {string} additionalSelectors - CSS selectors to add to the block list
 */
export const updateBlockSelector = (additionalSelectors) => {
  if (window.NREUM && window.NREUM.init && window.NREUM.init.session_replay) {
    const currentSelector = window.NREUM.init.session_replay.block_selector || '';
    const newSelector = currentSelector 
      ? `${currentSelector}, ${additionalSelectors}`
      : additionalSelectors;
    
    window.NREUM.init.session_replay.block_selector = newSelector;
    console.log('NewRelic block selector updated:', newSelector);
  }
};

/**
 * Get current NewRelic configuration for debugging
 * @returns {Object} Current NewRelic configuration
 */
export const getNewRelicConfig = () => {
  if (window.NREUM && window.NREUM.init) {
    return {
      sessionReplay: window.NREUM.init.session_replay,
      isNewRelicLoaded: !!window.newrelic,
      blockedSelectors: window.NREUM.init.session_replay?.block_selector || 'none'
    };
  }
  return { error: 'NewRelic not loaded' };
};